import * as fs from 'fs';
import * as path from 'path';

import { makeNamespacedLog } from '@sb/log';

const log = makeNamespacedLog('writeCaptureLocally');

/** Bytes a single path component may occupy on the filesystems we ship on. */
const MAX_FILENAME_BYTES = 255;

/**
 * The filename a local write may use, from whatever path the step resolved.
 *
 * `path.basename` is the guard, not a convenience: a local target joins this
 * onto a robot-configured directory, and `path.join(dir, '../../etc/x.png')`
 * escapes that directory without complaint. `basename` cannot return anything
 * containing a separator, so the join is confined by construction — the three
 * rejected values below are the residue it can still produce (`''` from a bare
 * `/`, and `.`/`..` from a path ending in one).
 *
 * A step's remote path is deliberately NOT reused wholesale here. An FTP or S3
 * target is bounded by its equipment, so an absolute operator path there is
 * both intentional and harmless; a directory on the robot has no such boundary.
 *
 * Applied by `writeCaptureLocally` to whatever it is handed, rather than left
 * to the caller. The join is the containment point, so the check belongs beside
 * it: a future caller that builds a name some other way is then covered without
 * having to know this exists.
 */
export function localCaptureFilename(resolvedPath: string): string {
  const filename = path.basename(resolvedPath);

  if (!filename || filename === '.' || filename === '..') {
    throw new Error(
      `Cannot write locally: "${resolvedPath}" has no usable filename`,
    );
  }

  // Most Linux filesystems cap a single path COMPONENT at 255 bytes, and the
  // `.part` suffix has to fit too. Rejected here rather than left to the
  // filesystem, because an `ENAMETOOLONG` from `copyFile` names neither the
  // step nor the expression that produced the name.
  if (Buffer.byteLength(filename) + '.part'.length > MAX_FILENAME_BYTES) {
    throw new Error(
      `Cannot write locally: filename is too long (${Buffer.byteLength(
        filename,
      )} bytes, limit ${MAX_FILENAME_BYTES - '.part'.length})`,
    );
  }

  return filename;
}

/**
 * Writes one already-staged file into the local capture directory.
 *
 * Via a temp file in the DESTINATION directory and a `rename`, because
 * `rename(2)` within one filesystem is atomic and this directory is read while
 * it is being written: a customer's QA system polls it, and pruning walks it.
 * Writing the final path directly would let either observe a half-written PNG,
 * and `sharp` writes progressively enough for that to be reachable rather than
 * theoretical.
 *
 * The temp lives in the destination directory rather than `os.tmpdir()` so the
 * rename stays within one filesystem. A cross-device rename fails with EXDEV,
 * which would make this fall back to a copy and lose the atomicity that is the
 * whole point.
 *
 * The `.part` suffix is what pruning skips: an in-progress write is not a
 * finished capture and must not be counted toward the budget or deleted as the
 * oldest thing present.
 */
export async function writeCaptureLocally(args: {
  stagedPath: string;
  directory: string;
  filename: string;
}): Promise<string> {
  const { stagedPath, directory } = args;

  const filename = localCaptureFilename(args.filename);

  await fs.promises.mkdir(directory, { recursive: true });

  const destination = path.join(directory, filename);
  const partial = `${destination}.part`;

  try {
    await fs.promises.copyFile(stagedPath, partial);
    await fs.promises.rename(partial, destination);
  } catch (error) {
    // The partial must not survive a failure: pruning skips `.part` files, so
    // one left behind would occupy the directory without ever being reclaimed.
    await fs.promises.unlink(partial).catch(() => {});

    throw error;
  }

  log.info('writeCaptureLocally', 'Wrote a capture locally', { destination });

  return destination;
}
